/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useEffect, useState } from 'react'

import { useApi } from '@/hooks'

import type { TrackerListItem } from './types'

/**
 * The trackers a customer currently holds (GET /trackers/list).
 *
 * Reports the failure rather than swallowing it: an empty dropdown reads as
 * "this customer has no devices", which sends you looking at the data when the
 * real cause is usually the API — a new route directory only registers on a
 * full restart, so until then this endpoint 404s.
 */
export const useTrackerList = (companyId: number | null) => {
  const { api } = useApi()
  const [trackers, setTrackers] = useState<TrackerListItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!companyId) {
      setTrackers([])
      setError('')
      return
    }
    let cancelled = false
    setIsLoading(true)
    setError('')
    api
      .get<TrackerListItem[]>({ path: `/trackers/list?companyId=${companyId}`, origin: 'NewShipmentPage' })
      .then((response) => {
        if (cancelled) return
        if (response.error) {
          setTrackers([])
          setError(
            response.code === 404
              ? 'Could not load devices — /trackers/list is not registered. Restart the API.'
              : response.message || 'Could not load devices'
          )
          return
        }
        setTrackers(response.data ?? [])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [api, companyId])

  return { trackers, isLoading, error }
}
